import type {
  NegotiationAuthDto,
  RateSeniorityLevelDto,
} from '@/api/negotiation-client';
import { SENIORITY_LEVELS_ORDERED } from '@/lib/operational-view-labels';

// =============================================================================
// negotiation-couple.ts — Dérivations PURES de l'ouverture de négociation au grain BINÔME.
// =============================================================================
// POURQUOI CE MODULE EXISTE (décision GATE_HUMAN du 2026-09-22, verbatim : « il faut déplacer seulement
// la fonctionnalité négociation sur la page […]/operational-view/fournisseurs ») : ces dérivations
// vivaient dans `analysis-fusion.ts`, c'est-à-dire dans la bibliothèque de la page d'ANALYSE, parce que
// l'ouverture s'y trouvait. L'ouverture a DÉMÉNAGÉ dans la vue opérationnelle ; les laisser là-bas
// aurait fait dépendre la vue opérationnelle de la bibliothèque d'un autre écran. Elles sont donc
// ISOLÉES ici, sans attache à un écran particulier.
//
// ZÉRO règle métier : l'autorité de l'ouverture, du périmètre autorisé et du verrouillage reste l'API
// (`POST …/negotiation/reopen`, `GET …/negotiation/auth`). Ce module ne fait que LIRE l'état relayé.

/**
 * Clé d'un binôme Fournisseur × Profil. Le niveau de séniorité n'y entre PAS : le grain d'OUVERTURE est
 * le binôme (décision GATE_HUMAN du 2026-09-17), donc deux lignes-niveaux d'un même profil partagent
 * une seule et même clé, un seul et même état.
 */
export function negotiationCoupleKey(
  supplierSiret: string,
  profileCode: string,
): string {
  return `${supplierSiret}::${profileCode}`;
}

/** État d'ouverture d'un binôme à la négociation. */
export type NegotiationCoupleState = 'closed' | 'open' | 'locked';

/**
 * Dérive l'état par binôme depuis `GET …/negotiation/auth` (règle A2 §5, le niveau de séniorité
 * n'entrant PAS dans la décision) :
 *   • aucune entrée pour ce SIRET, ou profil absent de `profilesAuthorized` ⇒ `closed` (ouvrable) ;
 *   • profil présent et `lockedFinal === false` ⇒ `open` (déjà ouverte, réouverture idempotente) ;
 *   • profil présent et `lockedFinal === true` ⇒ `locked` (nouvelle ouverture refusée 409).
 */
export function negotiationStateByCouple(
  auths: readonly NegotiationAuthDto[] | undefined,
): ReadonlyMap<string, NegotiationCoupleState> {
  const map = new Map<string, NegotiationCoupleState>();
  for (const auth of auths ?? []) {
    for (const profile of auth.profilesAuthorized) {
      map.set(
        negotiationCoupleKey(auth.supplierSiret, profile.profileCode),
        auth.lockedFinal ? 'locked' : 'open',
      );
    }
  }
  return map;
}

/** Rang d'affichage d'un niveau de séniorité (Débutant → Référent) ; inconnu ⇒ rejeté en fin de liste. */
function seniorityIndex(level: string): number {
  const index = (SENIORITY_LEVELS_ORDERED as readonly string[]).indexOf(level);
  return index === -1 ? SENIORITY_LEVELS_ORDERED.length : index;
}

/**
 * Niveaux de séniorité DISTINCTS d'un binôme, ordonnés Débutant → Référent.
 *
 * C'est la matérialisation du GRAIN D'OUVERTURE : ouvrir depuis UNE ligne ouvre TOUS les niveaux du
 * profil pour ce fournisseur (décision GATE_HUMAN du 2026-09-17, inchangée le 2026-09-22). La liste
 * sert à DEUX choses : l'intention envoyée à l'API, et le texte montré à l'utilisateur, qui doit dire
 * ce qui va réellement s'ouvrir plutôt que de laisser croire à une ouverture du seul niveau cliqué.
 */
export function coupleSeniorityLevels<
  T extends { readonly profileCode: string; readonly seniorityLevel: RateSeniorityLevelDto },
>(rows: readonly T[], profileCode: string): readonly RateSeniorityLevelDto[] {
  const levels = new Set<RateSeniorityLevelDto>();
  for (const row of rows) {
    if (row.profileCode === profileCode) {
      levels.add(row.seniorityLevel);
    }
  }
  return [...levels].sort((a, b) => seniorityIndex(a) - seniorityIndex(b));
}
